'use client';
import React, { FC, useEffect, useState } from 'react';
import {
  Button,
  ChevronIcon,
  ExternalLink,
  HeaderText,
  Loader,
  LuvImage,
} from '@/components';
import { BUTTON_SIZES, BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import { Modal } from '@/components/ui/Modal';
import { HeaderSize } from '@/components/ui/HeaderText';
import MuxPlayer from '@mux/mux-player-react';
import { TypeCourseSongFields } from '@/core/generated-types';
import { THEME_COLORS } from '@/core/constants';
import {
  formatTokens,
  RefetchTokenSetFn,
  RetrieveTokenSetFn,
} from '@/lib/api/playbackService/hooks/useTokenSetVault';
import useAuth from '@/context/AuthContext';
import { Entry } from 'contentful';
import { TypeSignedMuxAudioAsset } from '@/components/courses/CourseVideo/utils';
import { ANONYMOUS } from '@/utils/dataLayer';
import {
  ASSET_TYPES,
  generateMuxPlaybackEvents,
} from '@/utils/videoPlayerHelpers';

export interface TypeCourseSongProps extends TypeCourseSongFields {
  retrieveSongAssetFromVault: RetrieveTokenSetFn;
  fetchTokenSetForSongAsset: RefetchTokenSetFn;
  song: Entry<TypeSignedMuxAudioAsset>;
}

const CourseSong: FC<TypeCourseSongProps> = ({
  song,
  songImage,
  streamingExternalLinks,
  title,
  ctaText,
  fetchTokenSetForSongAsset,
  retrieveSongAssetFromVault,
}) => {
  const [isSongStreamingLinksModalOpen, setIsSongStreamingLinksModalOpen] =
    useState(false);

  const user = useAuth();
  const userUuid = user.user?.attributes['custom:account_manager_id'];

  useEffect(() => {
    const playbackId = song.fields.muxAudioUpload.signedPlaybackId;

    fetchTokenSetForSongAsset(playbackId, false);
    // the fetchTokenSetForSongAsset triggers an api request and this should only
    // run once when the component mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { songTitle, muxAudioUpload } = song.fields;
  const songAssetTokens = retrieveSongAssetFromVault(
    muxAudioUpload.signedPlaybackId,
  );

  const areTokensLoading = songAssetTokens?.token?.loading;

  function refetchTokenSet() {
    // note: we will limit retry attempts to 5 per mux asset to prevent hammering playback service
    // with requests if we somehow receive a barrage of error events from the player
    // see useTokenSetVault for more information
    if (songAssetTokens?.playbackId) {
      fetchTokenSetForSongAsset(songAssetTokens.playbackId, true);
    }
  }

  const eventProps = generateMuxPlaybackEvents(
    {
      title,
      duration: muxAudioUpload.duration,
      isSigned: true,
      type: ASSET_TYPES.AUDIO,
      viewerId: userUuid || ANONYMOUS,
    },
    {
      error: refetchTokenSet,
    },
  );

  return (
    <>
      <Button
        style={BUTTON_STYLES.link}
        size={BUTTON_SIZES.none}
        onClick={() => setIsSongStreamingLinksModalOpen(true)}
        classes="flex mt-2"
        ctaText={ctaText}
        testId="open-course-song-modal"
      />
      <Modal
        isOpen={isSongStreamingLinksModalOpen}
        handleClose={() => setIsSongStreamingLinksModalOpen(false)}
      >
        <div
          className="max-w-[328px] p-3"
          data-testid="course-song-player-container"
        >
          <div className="flex items-center">
            <LuvImage imageData={songImage} width={172} height={172} />
          </div>
          <HeaderText
            size={HeaderSize.H3}
            text={title}
            align="center"
            classes="mt-3 mb-2"
          />
          {areTokensLoading ? (
            <Loader loading />
          ) : (
            <MuxPlayer
              audio
              streamType="on-demand"
              playbackId={muxAudioUpload.signedPlaybackId}
              primaryColor={THEME_COLORS.primary}
              secondaryColor={THEME_COLORS.white}
              tokens={formatTokens(songAssetTokens)}
              metadata={{
                video_id: muxAudioUpload.signedPlaybackId,
                video_title: songTitle,
                viewer_user_id: userUuid,
                env_key: process.env.NEXT_PUBLIC_MUX_ENV_KEY,
                player_name: 'Web Course Song Player',
                // If updates made to the player, update me with either a minor or major version bump
                player_version: '1.0',
                video_content_type: 'short',
                video_producer: 'Lovevery',
              }}
              {...eventProps}
            />
          )}
          <div className="-mx-7 mb-4 mt-2 border-b border-grey-midtone" />
          {streamingExternalLinks?.map((link) => (
            <ExternalLink
              key={link.sys.id}
              link={link}
              classes="border border-grey-midtone rounded-md mb-3 px-4 py-2 justify-between"
            >
              <div className="flex items-center gap-2">
                {link.fields.linkImage && (
                  <figure className="inline-block">
                    <LuvImage
                      imageData={link.fields.linkImage}
                      width={36}
                      height={36}
                    />
                  </figure>
                )}
                <p className="text-sm">{link.fields.linkText}</p>
              </div>
              <ChevronIcon
                color="charcoal"
                direction="right"
                height={16}
                width={16}
                strokeWidth={2}
              />
            </ExternalLink>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default React.memo(CourseSong);
